import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './SimpleAuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { Input } from './components/ui/input';
import { Button } from './components/ui/button';
import { Label } from './components/ui/label';
import DemoModal from './components/DemoModal';
import {
  Mail, Lock, AlertCircle, ArrowRight, ArrowUpRight, ChevronRight, PlayCircle,
  Layers, Workflow, Coins, ShieldAlert, LineChart, Activity,
  Users, FileText, Cpu, Database, Gauge, FileSpreadsheet,
  Calculator, Building2, BadgeDollarSign, BarChart3,
} from 'lucide-react';

/* ------------------------------------------------------------------ *
 *  Animated counter — counts from 0 to target on first viewport entry
 * ------------------------------------------------------------------ */
function Counter({ to = 0, suffix = '', duration = 1.8, decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const mv = useMotionValue(0);
  const display = useTransform(mv, (v) =>
    decimals ? v.toFixed(decimals) : Math.round(v).toLocaleString()
  );

  useEffect(() => {
    if (inView) {
      const controls = animate(mv, to, { duration, ease: [0.2, 0.8, 0.2, 1] });
      return () => controls.stop();
    }
  }, [inView, to, duration, mv]);

  return (
    <span ref={ref} className="font-mono tabular-nums">
      <motion.span>{display}</motion.span>
      {suffix}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 *  Brand wordmark
 * ------------------------------------------------------------------ */
function Brand({ size = 'md' }) {
  const t = size === 'lg' ? 'text-2xl' : size === 'sm' ? 'text-base' : 'text-xl';
  return (
    <div className={`flex items-center gap-2.5 font-display font-extrabold ${t} tracking-tight`}>
      <div className="relative inline-flex h-9 w-9 items-center justify-center border border-foreground bg-background">
        <div className="absolute inset-1 border border-foreground/40" />
        <span className="relative font-mono text-[11px] font-bold text-emerald-glow">B.AI</span>
      </div>
      <span className="leading-none">
        BuildMetri<span className="text-emerald-glow">.AI</span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Bento module card
 * ------------------------------------------------------------------ */
function ModuleCard({ icon: Icon, code, title, desc, span = '', accent = false, mono }) {
  return (
    <div
      className={`group relative border border-border bg-card hover-lift p-5 sm:p-6 ${span} ${
        accent ? 'bg-foreground text-background' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase opacity-60">{code}</div>
        <Icon className={`h-5 w-5 ${accent ? 'text-emerald-glow' : 'text-foreground/70'}`} strokeWidth={1.5} />
      </div>
      <h3 className="mt-4 font-display text-xl sm:text-2xl font-semibold leading-tight">{title}</h3>
      <p className={`mt-2 text-sm leading-relaxed ${accent ? 'text-background/70' : 'text-muted-foreground'}`}>
        {desc}
      </p>
      {mono && (
        <div className="mt-4 font-mono text-xs tracking-tight text-emerald-glow">{mono}</div>
      )}
      <ArrowUpRight className="absolute bottom-4 right-4 h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
    </div>
  );
}

/* ------------------------------------------------------------------ *
 *  Counters strip
 * ------------------------------------------------------------------ */
const COUNTERS = [
  { to: 15,  suffix: '',  label: 'Sectors' },
  { to: 109, suffix: '',  label: 'Sub-sectors' },
  { to: 8,   suffix: '',  label: 'Lifecycle phases' },
  { to: 30,  suffix: '+', label: 'Currencies' },
  { to: 5,   suffix: '',  label: 'Contract types' },
  { to: 12,  suffix: '',  label: 'Estimation modules' },
  { to: 4,   suffix: '',  label: 'Criticality tiers' },
  { to: 100, suffix: '%', label: 'AI variance advisor' },
];

/* ------------------------------------------------------------------ *
 *  Main page
 * ------------------------------------------------------------------ */
const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError('Please fill in all fields'); return; }
    try {
      setError(''); setLoading(true);
      await login(email, password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to log in. Please check your credentials.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* ---------------------------------------------------------- *
       *  Top nav
       * ---------------------------------------------------------- */}
      <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 py-3.5">
          <Brand />
          <div className="hidden md:flex items-center gap-6 text-sm font-medium">
            <a href="#modules" className="hover:text-emerald-glow transition-colors" data-testid="nav-modules">Modules</a>
            <a href="#metrics" className="hover:text-emerald-glow transition-colors" data-testid="nav-metrics">Metrics</a>
            <a href="#why"     className="hover:text-emerald-glow transition-colors" data-testid="nav-why">Why BuildMetri</a>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/signup?trial=true">
              <Button
                variant="default"
                className="h-9 bg-foreground text-background hover:bg-foreground/85 font-semibold text-xs tracking-wider uppercase"
                data-testid="nav-start-trial-btn"
              >
                Start Free Trial <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ---------------------------------------------------------- *
       *  Hero — split: copy + login form
       * ---------------------------------------------------------- */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-grid pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-12 gap-10">
          {/* Left — copy */}
          <div className="lg:col-span-7">
            <div className="rise-in inline-flex items-center gap-2 border border-foreground/30 px-3 py-1 text-[10px] font-mono uppercase tracking-[0.25em]">
              <span className="h-1.5 w-1.5 bg-emerald-glow inline-block" /> AI · Engineering · Lifecycle
            </div>
            <h1 className="rise-in mt-6 font-display text-4xl sm:text-5xl lg:text-7xl font-bold leading-[0.95] tracking-tighter" style={{animationDelay:'80ms'}}>
              Project Lifecycle<br/>
              <span className="text-emerald-glow">Cost Estimation</span><br/>
              Suite, calibrated by AI.
            </h1>
            <p className="rise-in mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-muted-foreground" style={{animationDelay:'160ms'}}>
              From WBS &amp; resource planning to EVM, funding drawdown and AI variance advisors —
              <span className="text-foreground font-semibold"> BuildMetri.AI </span>
              compresses the entire engineering &amp; construction estimation stack into one rigorous, region-calibrated workspace.
            </p>

            <div className="rise-in mt-8 flex flex-wrap gap-3" style={{animationDelay:'240ms'}}>
              <Link to="/signup?trial=true">
                <Button className="h-12 px-6 bg-emerald-glow text-black hover:opacity-90 font-display font-semibold text-sm tracking-wider uppercase shadow-hard" data-testid="hero-start-trial-btn"
                  style={{ backgroundColor: 'hsl(151 100% 45%)' }}>
                  Start 7-day free trial <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Button
                type="button"
                onClick={() => setDemoOpen(true)}
                variant="outline"
                className="h-12 px-6 border-foreground text-foreground hover:bg-foreground hover:text-background font-display font-semibold text-sm tracking-wider uppercase inline-flex items-center gap-2"
                data-testid="hero-see-demo-btn"
              >
                <PlayCircle className="h-4 w-4" strokeWidth={1.75} /> See it in 30s
              </Button>
              <a href="#modules">
                <Button variant="outline" className="h-12 px-6 border-foreground text-foreground hover:bg-foreground hover:text-background font-display font-semibold text-sm tracking-wider uppercase" data-testid="hero-explore-modules-btn">
                  Explore 12 modules
                </Button>
              </a>
            </div>

            {/* terminal strip */}
            <div className="rise-in mt-10 border border-border bg-card p-4 max-w-xl font-mono text-[12px] leading-6" style={{animationDelay:'320ms'}}>
              <div className="flex items-center gap-2 text-muted-foreground">
                <span className="h-2 w-2 bg-emerald-glow inline-block" />
                <span className="uppercase tracking-[0.2em] text-[10px]">Live engine</span>
                <span className="ml-auto text-[10px]">v1.8 · BuildMetri.AI</span>
              </div>
              <div className="mt-2">
                <span className="text-muted-foreground">$</span> calc.estimate(<span className="text-emerald-glow">"infra/highway"</span>, GFA=82_000)
              </div>
              <div className="text-muted-foreground">→ phases=<span className="text-foreground">8</span> · resources=<span className="text-foreground">31</span> · cpi=<span className="text-emerald-glow">1.04</span> · usd_total=<span className="text-foreground">$ 14,820,310</span><span className="caret"></span></div>
            </div>
          </div>

          {/* Right — sign-in card */}
          <div className="lg:col-span-5">
            <div className="border border-foreground bg-card shadow-hard">
              <div className="border-b border-border bg-foreground text-background px-6 py-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-glow">/ Sign in</div>
                <h2 className="mt-1 font-display text-2xl font-bold">Resume your workspace</h2>
                <p className="text-xs text-background/70 mt-1">Estimates · WBS · EVM · Funding · Collaboration</p>
              </div>
              <div className="px-6 py-6">
                {error && (
                  <div className="mb-4 border border-destructive/40 bg-destructive/5 px-3 py-2 flex items-center gap-2 text-destructive">
                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                    <span className="text-xs">{error}</span>
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Email</Label>
                    <div className="relative mt-1.5">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <Input id="email" type="email" placeholder="you@firm.com" value={email}
                        onChange={(e) => setEmail(e.target.value)} className="pl-10 h-11 border-foreground/40 focus-visible:ring-1 focus-visible:ring-primary" required data-testid="login-email-input" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">Password</Label>
                    <div className="relative mt-1.5">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                      <Input id="password" type="password" placeholder="••••••••" value={password}
                        onChange={(e) => setPassword(e.target.value)} className="pl-10 h-11 border-foreground/40 focus-visible:ring-1 focus-visible:ring-primary" required data-testid="login-password-input" />
                    </div>
                  </div>
                  <div className="flex justify-end -mt-1">
                    <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-emerald-glow" data-testid="login-forgot-link">Forgot password?</Link>
                  </div>
                  <Button type="submit"
                    className="w-full h-11 font-display font-semibold text-sm tracking-wider uppercase"
                    style={{ backgroundColor: 'hsl(151 100% 38%)', color: '#000' }}
                    disabled={loading} data-testid="login-submit-btn">
                    {loading ? 'Authenticating…' : (<span className="inline-flex items-center gap-2">Sign In <ArrowRight className="h-4 w-4" /></span>)}
                  </Button>
                </form>
                <div className="my-5 flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.25em] text-muted-foreground">
                  <div className="flex-1 border-t border-border" /> New here <div className="flex-1 border-t border-border" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/signup">
                    <Button variant="outline" className="w-full h-11 font-display font-semibold text-xs tracking-wider uppercase border-foreground/40 hover:border-foreground" data-testid="login-signup-btn">Sign Up</Button>
                  </Link>
                  <Link to="/signup?trial=true">
                    <Button className="w-full h-11 font-display font-semibold text-xs tracking-wider uppercase bg-foreground text-background hover:bg-foreground/85" data-testid="login-trial-btn">Free Trial</Button>
                  </Link>
                </div>
                <p className="mt-4 text-[11px] font-mono text-center text-muted-foreground" data-testid="login-trial-note">
                  7 days · 3 calculations · full feature access
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- *
       *  Counters — animated metrics ribbon
       * ---------------------------------------------------------- */}
      <section id="metrics" className="border-b border-border bg-foreground text-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10 sm:py-14">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-glow">/ Coverage</div>
              <h2 className="mt-1 font-display text-2xl sm:text-3xl font-bold">The estimation surface, in numbers.</h2>
            </div>
            <div className="hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-background/60">
              <Activity className="h-3.5 w-3.5 text-emerald-glow" /> snapshot · v1.8
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-px bg-background/15 border border-background/15">
            {COUNTERS.map((c, i) => (
              <div key={c.label} className="bg-foreground p-5 sm:p-6 hover:bg-foreground/95 transition-colors" data-testid={`counter-${i}`}>
                <div className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-none text-emerald-glow">
                  <Counter to={c.to} suffix={c.suffix} />
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-background/65">{c.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- *
       *  Bento module grid
       * ---------------------------------------------------------- */}
      <section id="modules" className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-14 sm:py-20">
          <div className="mb-10 max-w-2xl">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-glow">/ The modules</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">One workspace, twelve estimation disciplines.</h2>
            <p className="mt-4 text-muted-foreground">From design level-of-effort to construction baselines, BuildMetri.AI removes the spreadsheet sprawl.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
            <ModuleCard icon={Calculator}  code="01 / CALC"  title="Calculator"        desc="Region-aware cost &amp; LoE engine across 15 sectors and 109 sub-sectors." mono="$/m² · hours · region multipliers" span="lg:col-span-2" accent />
            <ModuleCard icon={Workflow}    code="02 / WBS"   title="WBS &amp; Sub-phases" desc="Eight lifecycle phases broken into discipline-tagged sub-phases with inheritance." />
            <ModuleCard icon={Users}       code="03 / RES"   title="Resource Planning" desc="Crew composition, productivity overrides, multi-region split with weighted forex." />
            <ModuleCard icon={ShieldAlert} code="04 / RISK"  title="Risk &amp; Change"     desc="Auto-included risk library, change log impact, contingency rollup by criticality." />
            <ModuleCard icon={Coins}       code="05 / FUND"  title="Funding Drawdown"  desc="Editable monthly burn, AI revised burn from actual variance, push-back to schedule." mono="linear · front · back curves" />
            <ModuleCard icon={LineChart}   code="06 / EVM"   title="Earned Value"      desc="PV / EV / AC, SPI, CPI, EAC and TCPI with a glanceable health gauge." />
            <ModuleCard icon={Gauge}       code="07 / KPI"   title="KPI Dashboard"     desc="Cost, schedule, productivity and risk indices in a single high-density panel." />
            <ModuleCard icon={BarChart3}   code="08 / VIZ"   title="Visual Dashboard"  desc="Phase Gantt, cost breakdown, rate-sheet treemaps, exportable to PDF." span="lg:col-span-2" />
            <ModuleCard icon={FileSpreadsheet} code="09 / BLA" title="Baseline &amp; Actuals" desc="CSV bulk-import of actuals, AI corrective advisor, revised burn projection." />
            <ModuleCard icon={Users}       code="10 / COL"   title="Collaboration"     desc="Multi-user comments per WBS node, role-tagged status updates, audit log." />
            <ModuleCard icon={BadgeDollarSign} code="11 / TAX" title="Tax &amp; Policies"   desc="Region-specific tax stacks, incentives, year-on-year inflation projection." />
            <ModuleCard icon={Database}    code="12 / RATE"  title="Rate Sheets &amp; Forex" desc="28 material + 21 labour seed rates × 11 region multipliers · 30+ FX pairs." />
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- *
       *  Why BuildMetri / trust strip
       * ---------------------------------------------------------- */}
      <section id="why" className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-14 sm:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-glow">/ Why BuildMetri</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">Engineered for estimators who refuse to gamble.</h2>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              Spreadsheets break under the weight of multi-region, multi-phase, multi-contract reality.
              BuildMetri.AI is a deterministic calculation core wrapped in an AI variance advisor —
              auditable, region-calibrated, and defensible in front of clients and lenders.
            </p>
            <ul className="mt-6 space-y-3 text-sm">
              {[
                ['Deterministic core', 'Every cost line is traceable to a sub-sector × phase × region rate.'],
                ['AI on top, never inside', 'LLMs narrate variance and recommend corrective actions — they do not invent rates.'],
                ['Lifecycle-aware', 'Same model spans design LoE through construction baselines and EVM.'],
                ['Built-in forex',     '30+ currency pairs, editable, persisted per workspace.'],
              ].map(([h, b]) => (
                <li key={h} className="flex gap-3">
                  <ChevronRight className="mt-0.5 h-4 w-4 text-emerald-glow flex-shrink-0" strokeWidth={2} />
                  <div><span className="font-semibold">{h}.</span> <span className="text-muted-foreground">{b}</span></div>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-px bg-border border border-border">
              {[
                { icon: Cpu,        k: 'AI advisors',     v: 'Live',    sub: 'Variance · Narrative · Drawing' },
                { icon: Building2,  k: 'Sectors',         v: '15',      sub: 'Infra · O&G · Renewables · …' },
                { icon: Layers,     k: 'Sub-sectors',     v: '109',     sub: 'Granular sector taxonomy' },
                { icon: FileText,   k: 'Contract types',  v: '5',       sub: 'LSTK · Cost+ · EPC · DB · UR' },
                { icon: Coins,      k: 'Currencies',      v: '30+',     sub: 'Editable forex grid' },
                { icon: Activity,   k: 'Live ribbon',     v: 'Bloomberg-style', sub: 'Daily delta on rate sheets' },
              ].map(({ icon: I, k, v, sub }) => (
                <div key={k} className="bg-card p-6 hover-lift">
                  <I className="h-5 w-5 text-emerald-glow" strokeWidth={1.5} />
                  <div className="mt-4 font-display text-2xl sm:text-3xl font-bold tracking-tight">{v}</div>
                  <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{k}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- *
       *  Final CTA
       * ---------------------------------------------------------- */}
      <section className="border-b border-border bg-foreground text-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-glow">/ Get started</div>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight">Estimate your next project before lunch.</h2>
            <p className="mt-3 text-background/70 max-w-xl">7-day trial. Three full project calculations, every module unlocked, no credit card required.</p>
          </div>
          <div className="flex flex-wrap gap-3 md:justify-end">
            <Link to="/signup?trial=true">
              <Button className="h-12 px-7 font-display font-semibold text-sm tracking-wider uppercase shadow-hard-emerald" style={{ backgroundColor: 'hsl(151 100% 45%)', color: '#000' }} data-testid="cta-start-trial-btn">
                Start free trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/signup">
              <Button variant="outline" className="h-12 px-7 border-background text-background hover:bg-background hover:text-foreground font-display font-semibold text-sm tracking-wider uppercase" data-testid="cta-create-account-btn">
                Create account
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- *
       *  Footer
       * ---------------------------------------------------------- */}
      <footer className="bg-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <Brand size="sm" />
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
            © {new Date().getFullYear()} BuildMetri.AI · Project Lifecycle Cost Estimation Suite · Engineered for estimators
          </div>
        </div>
      </footer>

      {/* 30-second guided demo modal */}
      <DemoModal
        open={demoOpen}
        onClose={() => setDemoOpen(false)}
        onCta={() => { setDemoOpen(false); navigate('/signup?trial=true'); }}
      />
    </div>
  );
};

export default Login;
